import React, { useState } from 'react';
import { Form, Upload, Button, Alert, Spin, Typography, message, Modal } from 'antd';
import { FileTextOutlined, SyncOutlined } from '@ant-design/icons';
import { RequirementType } from '../types';
import requirementService from '../services/requirementService';
import { extractErrorMessage } from '../utils/errorHandler';

const { Text } = Typography;

interface Props {
  projectId: string;
  onSuccess?: (requirementDocUrl: string) => void;
  onRequestSync?: () => void;
  disabled?: boolean;
}

const DocumentRequirementUploader: React.FC<Props> = ({ projectId, onSuccess, onRequestSync, disabled = false }) => {
  const [requirementType, setRequirementType] = useState<RequirementType>(RequirementType.DOCUMENT);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);

  const getAcceptedFileTypes = () => {
    switch (requirementType) {
      case RequirementType.AUDIO: return '.mp3,.wav,.m4a';
      case RequirementType.VIDEO: return '.mp4,.avi,.mov';
      case RequirementType.FIGMA_JSON: return '.json';
      default: return '.pdf,.doc,.docx,.txt,.json';
    }
  };

  const handleFileChange = (info: any) => {
    if (info.file.status === 'removed') {
      setFile(null);
      return;
    }
    setFile(info.file.originFileObj || info.file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Map FIGMA_JSON to DOCUMENT for backend processing while keeping the original type for project metadata
      const backendRequirementType = requirementType === RequirementType.FIGMA_JSON 
        ? RequirementType.DOCUMENT 
        : requirementType;

      const uploadResponse = await requirementService.uploadRequirement(
        projectId, 
        backendRequirementType, 
        file,
        undefined, // images
        true       // replaceExisting — marks as reupload; old documents are preserved
      );
      if (!uploadResponse || uploadResponse.status !== 'success') {
        throw new Error('Failed to upload requirement. Please try again.');
      }

      // Auto-regenerate BRD from all requirement files (old + new)
      message.loading({ content: 'Regenerating BRD from all documents...', key: 'brd-regen', duration: 0 });
      try {
        await requirementService.processRequirement(projectId);
        message.destroy('brd-regen');
        message.success('BRD updated with new requirements');
      } catch (brdErr: any) {
        message.destroy('brd-regen');
        message.warning('Document uploaded but BRD regeneration failed. Please regenerate manually.');
        console.error('BRD regeneration error:', brdErr);
      }

      setFile(null);
      if (onSuccess) onSuccess(uploadResponse.data.fileKey || '');
      
      // Show sync prompt
      setSyncModalVisible(true);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: '32px' }}>
      <h3 style={{ marginBottom: '16px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
        Update Requirements Document
      </h3>
      
      <Spin spinning={loading}>
        <Form layout="vertical">
          <Form.Item 
            label={<span style={{ fontWeight: 500, color: '#495057' }}>Requirement Type <span style={{ color: '#dc3545' }}>*</span></span>}
            style={{ marginBottom: '20px' }}
          >
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button
                type={requirementType === RequirementType.DOCUMENT ? 'primary' : 'default'}
                onClick={() => setRequirementType(RequirementType.DOCUMENT)}
                disabled={disabled}
                style={{
                  backgroundColor: requirementType === RequirementType.DOCUMENT ? '#dc3545' : '#ffffff',
                  borderColor: requirementType === RequirementType.DOCUMENT ? '#dc3545' : '#dee2e6',
                  color: requirementType === RequirementType.DOCUMENT ? '#ffffff' : '#495057',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                icon={<FileTextOutlined />}
              >
                Document (Word, Text)
              </Button>
              {/* <Button
                type={requirementType === RequirementType.FIGMA_JSON ? 'primary' : 'default'}
                onClick={() => setRequirementType(RequirementType.FIGMA_JSON)}
                style={{
                  backgroundColor: requirementType === RequirementType.FIGMA_JSON ? '#dc3545' : '#ffffff',
                  borderColor: requirementType === RequirementType.FIGMA_JSON ? '#dc3545' : '#dee2e6',
                  color: requirementType === RequirementType.FIGMA_JSON ? '#ffffff' : '#495057',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                icon={<span>📋</span>}
              >
                Figma (JSON)
              </Button> */}
              <Button
                type={requirementType === RequirementType.AUDIO ? 'primary' : 'default'}
                onClick={() => setRequirementType(RequirementType.AUDIO)}
                disabled={disabled}
                style={{
                  backgroundColor: requirementType === RequirementType.AUDIO ? '#dc3545' : '#ffffff',
                  borderColor: requirementType === RequirementType.AUDIO ? '#dc3545' : '#dee2e6',
                  color: requirementType === RequirementType.AUDIO ? '#ffffff' : '#495057',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                icon={<span>🎵</span>}
              >
                Audio Recording (MP3, WAV)
              </Button>
              <Button
                type={requirementType === RequirementType.VIDEO ? 'primary' : 'default'}
                onClick={() => setRequirementType(RequirementType.VIDEO)}
                disabled={disabled}
                style={{
                  backgroundColor: requirementType === RequirementType.VIDEO ? '#dc3545' : '#ffffff',
                  borderColor: requirementType === RequirementType.VIDEO ? '#dc3545' : '#dee2e6',
                  color: requirementType === RequirementType.VIDEO ? '#ffffff' : '#495057',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                icon={<span>🎬</span>}
              >
                Video Recording (MP4, AVI)
              </Button>
            </div>
          </Form.Item>

          <Form.Item 
            label={<span style={{ fontWeight: 500, color: '#495057' }}>Upload New Requirement File <span style={{ color: '#dc3545' }}>*</span></span>}
            style={{ marginBottom: '20px' }}
          >
            <Upload.Dragger
              accept={getAcceptedFileTypes()}
              beforeUpload={() => false}
              maxCount={1}
              onChange={handleFileChange}
              fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}
              onRemove={() => setFile(null)}
              disabled={disabled}
              style={{
                background: disabled ? '#e9ecef' : '#f8f9fa',
                border: '2px dashed #dee2e6',
                borderRadius: '8px',
                padding: '40px 20px',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            >
              <p style={{ margin: '0 0 8px 0', fontSize: '48px' }}>📁</p>
              <p style={{ 
                margin: '0 0 4px 0', 
                color: '#6c757d', 
                fontSize: '14px',
                fontWeight: 500 
              }}>
                Click or Drag & drop file to this area to upload
              </p>
              {file && (
                <Text strong style={{ color: '#495057', fontSize: '14px' }}>
                  Selected file: {file.name}
                </Text>
              )}
            </Upload.Dragger>
          </Form.Item>

          {error && (
            <Alert 
              type="error" 
              message={error} 
              style={{ 
                marginBottom: 16,
                borderColor: '#dc3545',
                backgroundColor: '#f8d7da'
              }} 
            />
          )}

          <Form.Item>
            <Button
              type={file && !loading && !disabled ? 'primary' : 'default'}
              onClick={handleUpload}
              disabled={!file || loading || disabled}
              loading={loading}
              style={file && !loading && !disabled ? {
                backgroundColor: '#dc3545',
                borderColor: '#dc3545',
                padding: '8px 24px',
                height: 'auto'
              } : { 
                backgroundColor: '#f9f9f9',
                borderColor: '#e5e5e5',
                color: '#9ca3af',
                padding: '8px 24px',
                height: 'auto',
                cursor: 'not-allowed'
              }}
            >
              Update Document
            </Button>
          </Form.Item>
        </Form>
      </Spin>

      {/* Sync Confirmation Modal */}
      <Modal
        title="Sync Downstream Stages?"
        open={syncModalVisible}
        onCancel={() => {
          setSyncModalVisible(false);
          window.location.reload();
        }}
        footer={[
          <Button key="no" onClick={() => {
            setSyncModalVisible(false);
            window.location.reload();
          }}>
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            icon={<SyncOutlined />}
            onClick={() => {
              setSyncModalVisible(false);
              if (onRequestSync) onRequestSync();
            }}
            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
          >
            Yes
          </Button>
        ]}
      >
        <p style={{ margin: '16px 0', color: '#495057' }}>
          Do you want to sync all downstream stages with the updated requirements?
        </p>
        <p style={{ margin: '0', color: '#6c757d', fontSize: '13px' }}>
          <strong>Yes</strong> — Sync all downstream stages now.<br />
          <strong>No</strong> — Only update the BRD.
        </p>
      </Modal>
    </div>
  );
};

export default DocumentRequirementUploader;